<template>
  <div :class="`BookmarkCard ${tagType} ${iconName ? 'with-icon' : ''}`">
    <div v-if="iconName" class="BookmarkCard-icon">
      <b-icon :icon="iconName" size="is-large" :type="iconType" />
    </div>

    <div :class="`BookmarkCard-card ${clickContentCb ? 'clickable' : ''}`" @click="clickContent">
      <div class="BookmarkCard-tag">
        {{ $t(`tag-types.${tagType}`) }}
      </div>
      <div class="BookmarkCard-content">
        <b-skeleton v-if="isLoading" width="80%" height="50px" animated></b-skeleton>
        <slot v-else name="content"></slot>
      </div>
      <div class="BookmarkCard-footer">
        <b-skeleton v-if="isLoading" width="50%" animated></b-skeleton>
        <slot v-else name="footer"></slot>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue"
import { BIcon, BSkeleton } from "buefy"

export default defineComponent({
  name: "BookmarkCard",
  components: {
    BIcon,
    BSkeleton,
  },
  props: {
    tagType: {
      type: String,
      required: true,
    },
    iconName: {
      type: String,
      default: "",
    },
    iconType: {
      type: String,
      default: "",
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    clickContentCb: {
      type: Function,
      default: null,
    },
  },
  methods: {
    clickContent(): void {
      if (this.clickContentCb) {
        this.clickContentCb()
      }
    },
  },
})
</script>

<style lang="scss">
@import "@/styles/variables";

$card-height: 200px;
$conseil-card-height: 450px;
$actualite-card-height: 130px;
$card-width: 270px;
$conseil-tag-width: 170px;
$icon-container-height: 55px;
$footer-height: 55px;
$margin-top: 2rem;

.BookmarkCard {
  position: relative;
  height: $card-height;
  margin-top: $margin-top;

  &.with-icon {
    height: $card-height + $icon-container-height;
    margin-top: calc(#{$margin-top} + #{$icon-container-height});
  }
}

.BookmarkCard-card {
  width: $card-width;
  min-height: $card-height;
  border-radius: 10px;
  border: 1px solid $bookmarkcard-border-color;
}

.BookmarkCard-tag {
  width: $tag-width;
  height: $tag-height;
  border-radius: 0 0 10px 10px;
  font-weight: bold;
  color: #ffffff;
  text-transform: uppercase;

  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 0 0.25em 0.25em 0.25rem;
  margin-left: $tag-margin-left;

  box-shadow: 0 5px 3px 0 $bookmarkcard-border-color;
}

.BookmarkCard-content {
  min-height: calc(#{$card-height} - #{$footer-height} - #{$tag-height});
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 15px 10px;
}

.BookmarkCard-footer {
  height: $footer-height;
  border-top: 1px solid $bookmarkcard-border-color;

  display: flex;
  align-items: center;
}

.BookmarkCard-icon {
  border-radius: 51px 51px 0 0;
  height: $icon-container-height;
  width: $tag-width;
  margin-left: calc(#{$tag-margin-left} + 1px);
  position: absolute;
  top: calc(1px - #{$icon-container-height});

  color: #ffffff;
  display: flex;
  justify-content: center;
  align-items: center;
}

.BookmarkCard {
  &.invitation,
  &.join,
  &.projet,
  &.financement,
  &.nouveau-financement,
  &.charte-engagement {
    .BookmarkCard-tag,
    .BookmarkCard-icon {
      background-color: $ofb-bleu-1;
    }
  }

  &.invitation {
    min-height: 300px;

    .BookmarkCard-card {
      min-height: 300px;
    }

    .BookmarkCard-content {
      min-height: 210px;
    }
  }

  &.actualite-nationale,
  &.actualite-regionale {
    max-height: $actualite-card-height;
    height: max-content;
    margin-top: 0;

    .BookmarkCard-card {
      width: $actualite-card-width;
      max-height: $actualite-card-height;
      height: 100%;
      background-color: $modal-background-color;
    }

    .BookmarkCard-tag {
      text-transform: none;
      background-color: $actualite-nationale;
      padding-top: 5px;
    }

    .BookmarkCard-content {
      position: relative;
      top: -$tag-height;
      max-height: $actualite-card-height;
      height: 100%;
    }

    .BookmarkCard-footer {
      display: none;
    }
  }

  &.actualite-regionale {
    .BookmarkCard-tag {
      background-color: $actualite-regionale;
    }
  }

  &.nouveau-conseil,
  &.conseil-des-eleves {
    height: $conseil-card-height;

    .BookmarkCard {
      height: $conseil-card-height;

      &.with-icon {
        height: $conseil-card-height + $icon-container-height;
      }
    }

    .BookmarkCard-card {
      height: $conseil-card-height;
    }

    .BookmarkCard-content {
      min-height: calc(#{$conseil-card-height} - #{$tag-height});
    }

    .BookmarkCard-tag,
    .BookmarkCard-icon {
      background-color: $ofb-bleu-1;
      width: $conseil-tag-width;
    }

    .BookmarkCard-footer {
      display: none;
    }
  }

  &.conseil-des-eleves {
    .BookmarkCard-tag,
    .BookmarkCard-icon {
      width: $conseil-tag-width + 15px;
    }
  }

  &.validation {
    .BookmarkCard-tag,
    .BookmarkCard-icon {
      background-color: $orange-v2;
    }
  }

  &.projet {
    .BookmarkCard-tag {
      font-size: 0.8em;
    }
  }
}
</style>
